import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { EDiscountTypes, IState, ECampaignStatus, EPromotionTypes } from "store/store.interface";
import Stepper from "components/Stepper";
import Layout from "Layout";
import PromotionForm, { EFormTypes } from "components/PromotionForm";
import _ from "lodash";
import { ExclusionLabel, IFormDetails, TFieldType, ECampaignMode } from "store/reducers/promotion.reducers";
import { setLocation } from "store/actions/auth.actions";
import Loader from "Loader/Form";
import { fetchFieldsDetails, setPromotionObject } from "store/actions/promotion.actions";
import Section from "Layout/Section";
import { isInvalidTimeWindow } from "components/PromotionForm/PromotionRecurrence/recurrenceHelpers";
import { REWARD_TYPES, NO_REWARD_INFO } from "components/PromotionForm/promotion.constants";

type StepConfig = {
  id: string;
  title: string;
  allowedCountries?: string[]; 
};

const PromotionCreate: FC = () => {
  const {
    creationForm,
    formDetails = {},
    roles = [],
    canCreateCampaigns = [],
    visibilityByCountry = {
      visibilityCountries: [],
      badgeCountries: [],
    },
    storeId,
    campaignMode,
    selectOptions,
    milestoneCampaigns = [],
    restrictionBlockErrors = {},
  } = useSelector((state: IState) => ({
    ...state.promotion,
    ...state.auth,
    ...state.config,
  }));
  const isRecurring = campaignMode === ECampaignMode.RECURRING;
  const [currentStep, setCurrentStep] = useState(1);

  const dispatch = useDispatch();

  const getStepsByCampaignType = (type: EPromotionTypes) => {
    const baseSteps = [
      { id: "basic", stepNumber: 1, title: "Basic Information" },
      { id: "restrictions", stepNumber: 2, title: "Restrictions" },
    ];

    const extraStepsConfig: StepConfig[] = [
      { id: "visibility", title: "Controls" , allowedCountries: visibilityByCountry?.visibilityCountries},
      { id: "badge", title: "Badge", allowedCountries: visibilityByCountry?.badgeCountries },
    ];

    const summaryStep = { id: "summary", title: "Summary" };

    const extraSteps = extraStepsConfig.filter(step =>
    !step.allowedCountries || (storeId && step.allowedCountries.includes(storeId)))

    if ([EPromotionTypes.COUPON, EPromotionTypes.DISCOUNT_COUPON,EPromotionTypes.PROMOTION_CALENDAR].includes(type)) {
      return [...baseSteps, ...extraSteps, summaryStep].map((s, i) => ({
        ...s,
        stepNumber: i + 1,
      }));
    }

    return [...baseSteps, summaryStep].map((s, i) => ({
      ...s,
      stepNumber: i + 1,
    }));
  };

  const stepsOptions = useMemo(() => {
    if (!creationForm?.type) return [];
    return getStepsByCampaignType(creationForm.type);
  }, [creationForm?.type]);

  const canCreateCampagin = useMemo(() => {
    const role = roles[0];
    return canCreateCampaigns && canCreateCampaigns.indexOf(role) > -1;
  }, [roles, canCreateCampaigns]);

  const [formErrors, setFormErrors] = useState<{
    [key: string]: { isError: boolean; message?: string };
  }>({});
  const { formValid ,incrementalData,incrementalType} = useSelector((state: IState) => state.promotion);
  const [disable, setDisable] = useState<boolean>(false);

  const [registeredFields, setRegisteredFields] = useState([] as TFieldType[]);

  const onRegisterField = (field: TFieldType) => {
    if (registeredFields.filter(({ id }) => id === field.id).length === 0) {
      setRegisteredFields((oldState: TFieldType[]) => [...oldState, field]);
    }
  };

  useEffect(() => {
    const form = JSON.parse(JSON.stringify(creationForm));
    if (
      creationForm.edit &&
      [
        ECampaignStatus.active,
        ECampaignStatus.completed,
        ECampaignStatus.paused,
        ...(creationForm?.rewardType === REWARD_TYPES.MILESTONE ? [ECampaignStatus.pending] : []),
      ].includes(form?.status)
    ) {
      setDisable(true);
    }
  }, [creationForm])



  const onUnRegisterField = (id: string) => {
    // not checking if was exist , because the new state not accessible from the useState
    // but exist inside the setRegisteredFields only using the oldState variable
    setRegisteredFields((oldState: TFieldType[]) => {
      return oldState.filter((field) => field.id !== id);
    });
  };

  const rangeValueHandler = useCallback(()=>{
    const isStepUp = creationForm?.rewardType === REWARD_TYPES.STEPUP;
    const isRewardBased = creationForm?.rewardType === REWARD_TYPES.MILESTONE;
    let updateValues : any[] =[];
    let maxValues = "";
    if(incrementalData && !isRewardBased){
     incrementalData && incrementalData.forEach((res:any,index:number)=>{
      maxValues = res.maxAmount;
      updateValues.push({
        discount:{
          type: incrementalType,
          value: parseInt(res.value),
          ...((isStepUp && incrementalType ==="PERCENTAGE") && {maxAmount: parseInt(res?.maxDiscountAmount)}),
        },
        ...(isStepUp && {step: index + 1}),
        ...(!isStepUp && {slot: [parseInt(res?.min, 10), parseInt(res?.max, 10)]}),
      })
      dispatch(setPromotionObject(updateValues, ['discount', 'rules', 'metaData', 'discountRanges']));
      if(incrementalType ==="PERCENTAGE" && !isStepUp){
        dispatch(setPromotionObject(parseInt(maxValues), ['discount', 'rules', 'metaData', 'discountRanges', `${incrementalData.length-1}`,'discount', 'maxAmount']));
      }
    })
    }
    if (isRewardBased && incrementalData) {
      const updateValues = incrementalData.map((res: any, index: number) => {
        const rewardType = selectOptions?.rewardCampaignTypes?.find((option: any) => option.value === res?.typeOfReward?.value)?.type;
        const campaign = milestoneCampaigns[index]?.find((campaign:any)=> campaign.id === res?.reward?.id);
        return {
        restrictions: {
          minOrderValue: Number.parseInt(res.rewardMov, 10),
        },
        discount: res?.typeOfReward?.value === NO_REWARD_INFO.id ? null : {
          type: rewardType,
          mode: res?.typeOfReward?.valueType,
          id: res?.reward?.id,
          name: res?.reward?.name,
          ...(campaign?.code && { code: campaign.code })
        },
        step: index + 1,
      }
      });
      dispatch(setPromotionObject(updateValues, ["discount","rules","metaData","discountRanges"]));
    }
  },[dispatch,incrementalData,incrementalType]);

  const onValidateInputs = (
    errorObj: { [key: string]: { isError: boolean; message?: string } } = {}
  ) => {
    setFormErrors((oldState) => ({ ...oldState, ...errorObj }));
  };
  
  const onNextHandler = () => {
    if (currentStep < stepsOptions?.length && canGoNext) {
      setCurrentStep((oldStep) => oldStep + 1);
    }
    if(currentStep===1){
      rangeValueHandler();
    }
  };

  const onBackHandler = () => {
    if (currentStep > 1) {
      setCurrentStep((oldStep) => oldStep - 1);
    } else {
      dispatch(setLocation(isRecurring ? "/recurring-coupons" : "/"));
    }
  };

  const getValue: any = useCallback(
    (keys = []) => {
      const value = _.get(creationForm, keys.join(".")) || "";
      return value;
    },
    [creationForm]
  );

  // /**
  //  * function to check if the registered values is required and has value
  //  */
  const validateFields = useCallback(
    (shouldRegisterErrors: boolean) => {
      let updatedErrors: {
        [key: string]: { isError: boolean; message?: string };
      } = { ...formErrors };
      registeredFields.forEach((field) => {
        const { isRequired, keys, startDateKeys, endDateKeys, name, atLeast } = field;
        let isError = false;
        if (isRequired) {
          const value = getValue(keys);
          const startDateValue = getValue(startDateKeys);
          const endDateValue = getValue(endDateKeys);        
          if (name === "dateRange") {
            if (
              !startDateValue ||
              !endDateValue ||
              startDateValue < new Date()
            ) {
              isError = true;
            }
          } else if (name === "recurrence") {
            const type = value?.type;
            if (type === "DAILY") {
              const timeWindow = value?.timeWindow;
              if (!timeWindow?.length || isInvalidTimeWindow(timeWindow)) {
                isError = true;
              }
            } else if (type === "WEEKLY") {
              const timeWindow = value?.timeWindow || [];
              const daysOfWeek = value?.weekly?.daysOfWeek || [];
              if (
                !daysOfWeek?.length ||
                isInvalidTimeWindow(timeWindow)
              ) {
                isError = true;
              }
            } else if (type === "MONTHLY") {
              const {
                dates,
                option,
                weekDays: { weekPosition = [], daysOfWeek = [] } = {},
              } = value?.monthly || {};
              const timeWindow = value?.timeWindow || [];
              if (
                (!dates?.length && option !== "SPECIFIC_WEEKDAYS") ||
                isInvalidTimeWindow(timeWindow)
              ) {
                isError = true;
              }
              if (option === "SPECIFIC_WEEKDAYS") {
                if (
                  !weekPosition?.length ||
                  !daysOfWeek?.length ||
                  isInvalidTimeWindow(timeWindow)
                ) {
                  isError = true;
                }
              }
            }
          } else if (!value) {
            isError = true;
          }
        }
        if(atLeast) {
          const haveValue = atLeast?.map(res => getValue(res.keys).length > 0 ? true : false);
          if(haveValue.includes(true)) {
            isError = false;
          }
        }
        if (
          (formErrors &&
            formErrors[name] &&
            !formErrors[name].isError &&
            !isError) ||
          isError
        ) {
          updatedErrors = { ...updatedErrors, ...{ [name]: { isError } } };
        }
      });
      if (shouldRegisterErrors) {
        setFormErrors((oldErrors) => ({
          ...oldErrors,
          ...updatedErrors,
        }));
      }
      return (
        Object.keys(updatedErrors).filter(
          (key) => updatedErrors[key as string].isError
        ).length === 0
      );
    },
    [getValue, registeredFields, formErrors]
  );

  const FormValidationHandler = useCallback(()=>{
    let isError = false;
    const validationValue = validateFields(false);
    if(creationForm.discount?.type === "INCREMENTAL" || creationForm.discount?.type === "MILESTONE"){
      if(formValid){
        if(validationValue){
          isError = true;
        }else{
          isError = false;
        }
      }else{
        isError = false;
      }
    }else{
      if(validationValue){
        isError= true;
      }else{
        isError = false
      }
    }
    return isError;
  },[creationForm.discount,formValid,validateFields]);

  const canGoNext = useMemo(() => {
    const { restrictions, discount, visibilityRules, rewardType } = creationForm;
    const { restrictions: formRestrictions } = (formDetails || {}) as IFormDetails;
    if (currentStep === 1) {
      return FormValidationHandler();
    } else if (currentStep === 2) {
      if (Object.values(restrictionBlockErrors).some(Boolean)) {
        return false;
      }
      if (restrictions && Object.keys(restrictions).length > 0) {
        let validations = [] as boolean[];
        formRestrictions.forEach((restriction) => {
          const { fields } = restriction;
          fields.forEach((field) => {
            const { isRequired, name, id, when } = field;
            if (isRequired) {
              const value = getValue(["restrictions", name]);
              if (value) {
                validations.push(true);
              } else {
                if(id === 'bin_lock_coupon_1') {
                  if(when.length > 0) {
                    when.forEach(({keys, values}: any)=> {
                      if(values.indexOf(creationForm.couponType) > -1) {
                        validations.push(false)
                      } else {
                        validations.push(true)
                      }
                    })
                  }
                } else {
                  validations.push(false);
                }
              }
            } else if (
              (discount?.type === EDiscountTypes.FREE_DELIVERY &&
                name === "shipmentType" &&
                !restrictions["shipmentType"]) ||
              (name === "minOrderValue" && restrictions["minOrderValue"] === 0)
            ) {
              validations.push(false);
            }
            validations.push(true);
          });
        });
        if (
          creationForm.type === EPromotionTypes.FEES_WAIVER_CAMPAIGN &&
          !restrictions["shipmentType"] &&
          !restrictions["sellerId"]
        ) {
          validations.push(false);
        }
        if (
          creationForm.type === EPromotionTypes.BADGE_CAMPAIGN &&
          !restrictions["sku"]
        ) {
          validations.push(false);
        }
        if (
          creationForm?.application?.priceCut &&
          !restrictions["sku"]
        ) {
          validations.push(false);
        }
        if(creationForm.type === EPromotionTypes.PROMOTION_CALENDAR 
          && restrictions["numberOfUsage"] && !restrictions["usageType"]){
          validations.push(false);
        }
        if(creationForm.type === EPromotionTypes.PROMOTION_CALENDAR 
          && restrictions["budget"] && !restrictions["budgetType"]){
          validations.push(false);
        }
        return validations.filter((validation) => !validation).length === 0;
      }
    } else if (currentStep === 3) {
      if ([EPromotionTypes.COUPON, EPromotionTypes.DISCOUNT_COUPON, EPromotionTypes.PROMOTION_CALENDAR].includes(creationForm.type!)) {
        const visibilityRules = creationForm.visibilityRules || {};
        // no rules at all then cannot proceed
        if (Object.keys(visibilityRules).length === 0) {
          return false;
        }
        if (rewardType === REWARD_TYPES.MILESTONE) {
          return FormValidationHandler();
        }
        return true;
      }
    } else if (currentStep === 4) {
      const isUserRestrictionApplied = restrictions?.isCustomerFirstOrder === true || 
      (typeof restrictions?.customerEmailGroup === "string" && restrictions?.customerEmailGroup) ||
      restrictions?.userSegment;

      const isSkuInclusionAllowed = Array.isArray(restrictions?.sku) || restrictions?.sku?.op === ExclusionLabel.in;
      const isShowOnPdpAndListing = visibilityRules?.showOnPdpAndListing;
      
      if ([EPromotionTypes.COUPON, EPromotionTypes.DISCOUNT_COUPON, EPromotionTypes.PROMOTION_CALENDAR].includes(creationForm.type!) 
        && !isUserRestrictionApplied && isSkuInclusionAllowed && isShowOnPdpAndListing) {
        return FormValidationHandler();
      }
      return true;
    }
    return false;
  }, [creationForm, FormValidationHandler, currentStep, formDetails, getValue, isRecurring, restrictionBlockErrors]);

  const goToBasicInfoSection = () => {
    setCurrentStep(1);
  };

  const goToRestrictionsSection = () => {
    setCurrentStep(2);
  };

  const currentStepConfig = stepsOptions?.[currentStep - 1];

  const formType = useMemo(() => {
    switch (currentStepConfig?.id) {
      case "basic":
        return EFormTypes.BASIC;
      case "restrictions":
        return EFormTypes.RESTRICTIONS;
      case "visibility":
        return EFormTypes.VISIBILITY;
      case "badge":
        return EFormTypes.BADGE;
      case "summary":
        return EFormTypes.SUMMARY;
      default:
        return EFormTypes.BASIC;
    }
  }, [currentStepConfig?.id]);

  const noForm = useMemo(
    () => !formDetails || Object.keys(formDetails).length === 0,
    [formDetails]
  );

  const campaignType = useMemo(() => {
    return creationForm.type;
  }, [creationForm]);

  useEffect(() => {
    setRegisteredFields([]);
    setFormErrors({});
  }, [campaignType]);

  useEffect(() => {
    const { type } = creationForm || {};
    if (!type) {
      dispatch(setLocation( isRecurring ? "/recurring-coupons" : "/"));
    } else if (noForm) {
      setCurrentStep(1);
      dispatch(fetchFieldsDetails());
    }
  }, [noForm, dispatch, creationForm, isRecurring]);

  useEffect(() => {
    if (!canCreateCampagin) {
      dispatch(setLocation("/"));
    }
  }, [canCreateCampagin, dispatch]);

  // This is used to normalize the restrictions numberOfUsage and budget fields when recurring campaign  
  useEffect(() => {
    const restrictions = creationForm?.restrictions;
    if (!restrictions && campaignMode !== ECampaignMode.RECURRING) return;

    const patch = {
      ...(typeof restrictions.numberOfUsage === "object" && {
        numberOfUsage: restrictions.numberOfUsage.value,
        usageType: restrictions.numberOfUsage.type,
      }),
      ...(typeof restrictions.budget === "object" && {
        budget: restrictions.budget.amount,
        budgetType: restrictions.budget.type,
      }),
    };

    if (!Object.keys(patch).length) return;

    dispatch(
      setPromotionObject({ ...restrictions, ...patch }, ["restrictions"]),
    );
  }, [currentStep, creationForm, campaignMode, dispatch]);

  // This is used to normalize the voucher type and other vocher type
  useEffect(() => {
    const type = creationForm?.tags?.type;
    const isOtherVoucherType = type && !selectOptions?.voucherTypes?.some((option: any) => option?.value === type);

    if (isOtherVoucherType) {
      dispatch(setPromotionObject({ type: "Others", customVoucherType: type }, ["tags"]));
    }
  }, [currentStep, creationForm, dispatch]);

  if (noForm) {
    return (
      <Layout>
        <Section>
          <Loader withStepper />
        </Section>
      </Layout>
    );
  }
  return (
    <Layout>
      <Section>
        <Stepper currentStep={currentStep} steps={stepsOptions} />
        <PromotionForm
          disable={disable}
          formType={formType}
          onNext={onNextHandler}
          onBack={onBackHandler}
          onValidateInputs={onValidateInputs}
          registerField={onRegisterField}
          unRegisterField={onUnRegisterField}
          fieldsErrors={formErrors}
          getValue={getValue}
          canGoNext={canGoNext}
          onEditBasicInfo={goToBasicInfoSection}
          onEditRestrictions={goToRestrictionsSection}
          isLastStep={
            currentStep === stepsOptions?.[stepsOptions?.length - 1]?.stepNumber
          }
        />
      </Section>
    </Layout>
  );
};

export default PromotionCreate;
